import { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';

import { useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import {
  MENU_NOTIFICATION_EVENT,
  MENU_NOTIFICATION_TYPE,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';
import { fetchCubohItemChildren, getReadableItemTypePlural } from 'helpers/menuManagement';
import { logException } from 'helpers/loggers';
import { trackMenuAvailabilityToggled, trackMenuEntityAction } from 'helpers/menuAnalytics';
import { useMenuManagement } from 'hooks/useMenuManagement';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import {
  canEditMasterMenuItems,
  getCloneMenuDisabledTooltip,
  getMasterMenuDisabledTooltip,
  getPosSyncRowDisabledTooltip,
} from 'helpers/masterMenuPermissions';

import { BADGE_VARIANTS } from 'constants/mantineVariants';
import {
  Accordion,
  Badge,
  Card,
  Center,
  Flex,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { DisabledReasonTooltip } from './DisabledReasonTooltip';
import { PosSyncLockoutIcon } from './PosSyncLockoutIcon';
import { RenderActions } from 'components/MenuManagement/MenuItemActions/RenderActions';



import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import renderSubItems from './SubItem/renderSubItems';
import { EditableText } from './EditableText';
import { INVISIBLE_CHEVRON } from './constants';
import { ChevronDownCircleFilledIconSm } from '@chownow/pantry/icons';

export default function CategoryTree({ category, order, searchUuid }) {
  const { menu } = useContext(MenuEdit.Context);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const categoryUuidSuffix = category.uuid.slice(-6);

  const { updateCubohMenu, getMerchantIntegrationsFromState } = useMenuManagement();
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();

  const [children, setChildren] = useState();
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);

  const hasLimitedAccess =
    menu?.masterMenuPermissionTier && !canEditMasterMenuItems(menu.masterMenuPermissionTier);
  const isPosSyncInPlace = !!getMerchantIntegrationsFromState(category.merchantId || '')
    ?.isPosSyncInPlace;
  const shouldDisableEdit = !!category.noEdit || hasLimitedAccess || isPosSyncInPlace;

  const getDisabledTooltip = () => {
    if (hasLimitedAccess) return getMasterMenuDisabledTooltip();
    if (isPosSyncInPlace) return getPosSyncRowDisabledTooltip('category');
    if (category.noEdit) {
      return getCloneMenuDisabledTooltip(getReadableItemTypePlural(category?.itemType));
    }
    return null;
  };

  const handleFetchChildren = async () => {
    setIsLoadingChildren(true);
    try {
      const response = await fetchCubohItemChildren({
        companyID: category.companyId,
        merchantID: category.merchantId,
        parentUuid: category.uuid,
        searchUuid,
      });
      setChildren({
        itemType: response.itemType,
        children: response.items,
      });
    } catch (e) {
      handleErrorNotification({
        customMessage: `We could not load your ${getReadableItemTypePlural(category?.itemType)}. Please try again.`,
      });
    } finally {
      setIsLoadingChildren(false);
    }
  };

  useEffect(() => {
    const handleNotification = event => {
      if (
        event.detail.type === MENU_NOTIFICATION_TYPE.SUCCESS &&
        (event.detail.id === category.uuid || event.detail.parentUuid === category.uuid)
      ) {
        handleFetchChildren();
      }
    };

    window.addEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
    return () => window.removeEventListener(MENU_NOTIFICATION_EVENT, handleNotification);
  }, [category, searchUuid]);

  /* The toggle stays available on a POS sync-in-place menu (CN-50652) - hiding a category is
  merchant-owned, unlike its content. A failed update has to surface: updateCubohMenu rethrows,
  and an unhandled rejection would just snap the switch back with no explanation. */
  const handleToggleEnable = async event => {
    const isEnabled = event.currentTarget.checked;
    try {
      await updateCubohMenu({
        group: category.itemType,
        id: category.key,
        data: { ...category, enabled: isEnabled },
      });
    } catch (e) {
      const readableItemType = TYPE_TO_READABLE_STRING_MAP[category?.itemType]?.toLowerCase();
      handleErrorNotification({ readableItemType });
      // logException replaces the error with its own message, so pass the reason via context.
      logException({
        message: 'Failed to toggle category availability',
        error: e,
        context: {
          reason: e instanceof Error ? e.message : String(e),
          itemType: category.itemType,
          uuid: category.uuid,
        },
        tags: null,
      });
      return;
    }

    trackMenuAvailabilityToggled({
      entityType: 'category',
      availability: isEnabled ? 'enabled' : 'disabled',
      menuId: menu?.uuid,
      entityId: category.uuid,
    });
  };

  const handleOnEdit = async (textKey, updatedText) => {
    if (shouldDisableEdit) return;

    const readableItemType = TYPE_TO_READABLE_STRING_MAP[category?.itemType].toLowerCase();

    try {
      await updateCubohMenu({
        group: category?.itemType,
        id: category.key,
        data: { ...category, [textKey]: updatedText },
      });

      trackMenuEntityAction({
        action: 'update',
        entityType: 'category',
        menuId: menu?.uuid,
        entityId: category.uuid,
        fieldsChanged: textKey,
      });

      handleSuccessNotification({ readableItemType });
    } catch (e) {
      handleErrorNotification(readableItemType);
    }
  };

  const categoryHasChildren = !!category.children.length || !!children?.children?.length;

  const badgeEl = (
    <Badge
      variant={BADGE_VARIANTS.ink}
      h="var(--dimension-sm)"
      style={{ overflow: 'visible' }}
      size="xs"
      w="var(--dimension-xl5)"
    >
      category {order}
    </Badge>
  );

  const titleEl = (
    <DisabledReasonTooltip label={getDisabledTooltip()} disabled={!shouldDisableEdit}>
      <EditableText
        rawText={category.name}
        subText={categoryUuidSuffix}
        text={<Text variant="title-sm">{category.name}</Text>}
        textKey="name"
        handleEdit={handleOnEdit}
        isRequired
        disableEdit={shouldDisableEdit}
      />
    </DisabledReasonTooltip>
  );

  /* Under POS sync-in-place the category's content is the POS's, so the actions menu gives way
  to the lockout icon - but the enabled/disabled toggle stays (CN-50652). */
  const actionsEl = (
    <Flex gap="xs2" align="center">
      {isPosSyncInPlace ? (
        <PosSyncLockoutIcon label={getPosSyncRowDisabledTooltip('category')} />
      ) : (
        <RenderActions record={category} group={MENU_OBJECT_TYPES.categories} showAdd showEdit />
      )}
      {/* Always editable for cloned/master items */}
      <Tooltip
        label={
          category.enabled
            ? 'Toggle to hide on menu. You will need to toggle again to unhide.'
            : 'Toggle to show on menu'
        }
        disabled={isMobile}
        position="top"
        w="auto"
        maw="220px"
      >
        <div>
          <Switch
            size="xs"
            checked={category.enabled}
            onChange={event => {
              void handleToggleEnable(event);
            }}
          />
        </div>
      </Tooltip>
    </Flex>
  );

  return (
    <Card p="0" withBorder radius="xs" variant="outline-secondary">
      <Accordion
        chevronPosition="left"
        chevron={categoryHasChildren ? <ChevronDownCircleFilledIconSm /> : INVISIBLE_CHEVRON}
        variant="separated"
        onChange={value => value !== null && handleFetchChildren()}
      >
        <Accordion.Item
          value={category.name}
          bd="none"
          my="md"
          style={{ backgroundColor: 'transparent' }}
        >
          {isMobile ? (
            <Flex px="sm" gap="xs" align="flex-start">
              <Accordion.Control
                style={{ opacity: '1', cursor: 'initial' }}
                w="auto"
                styles={{
                  chevron: { marginInlineStart: 0, marginInlineEnd: 0, alignItems: 'center' },
                  label: { display: 'none' },
                  control: {
                    backgroundColor: 'transparent',
                    height: 'var(--dimension-sm)',
                    padding: 0,
                    paddingTop: 'var(--mantine-spacing-xs3)',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
              <Stack flex={1} gap="sm" miw={0}>
                <Flex justify="space-between" align="center" gap="xs">
                  {badgeEl}
                  {actionsEl}
                </Flex>
                <Stack gap="xs3">{titleEl}</Stack>
              </Stack>
            </Flex>
          ) : (
            <Flex
              direction="row"
              bg="var(--surface-primary)"
              align="center"
              gap="xs"
              pr="md"
              w="100%"
            >
              <Accordion.Control
                style={{ opacity: '1', cursor: 'initial' }}
                bg="var(--surface-primary)"
                pr={0}
                w="auto"
                styles={{ chevron: { marginInlineEnd: 0 }, label: { display: 'none' } }}
              />
              <Group justify="space-between" gap="xs" w="100%" wrap="nowrap">
                <Flex direction="row" align="center" gap="xs" flex={1} miw={0}>
                  {badgeEl}
                  <Stack gap="xs3" flex={1} miw={0}>
                    {titleEl}
                  </Stack>
                </Flex>
                {actionsEl}
              </Group>
            </Flex>
          )}
          {categoryHasChildren && (children || isLoadingChildren) && (
            <Accordion.Panel
              styles={{
                content: {
                  marginTop: 'var(--mantine-spacing-md)',
                  paddingBottom: 0,
                },
              }}
            >
              {isLoadingChildren && !children && (
                <Center my="md">
                  <Loader />
                </Center>
              )}
              {children && renderSubItems({ item: children })}
            </Accordion.Panel>
          )}
        </Accordion.Item>
      </Accordion>
    </Card>
  );
}

CategoryTree.propTypes = {
  category: PropTypes.shape({
    children: PropTypes.arrayOf(PropTypes.shape({})),
    companyId: PropTypes.number,
    description: PropTypes.string,
    enabled: PropTypes.bool,
    itemType: PropTypes.string,
    key: PropTypes.string,
    merchantId: PropTypes.number,
    name: PropTypes.string,
    noEdit: PropTypes.bool,
    ordinal: PropTypes.number,
    seqId: PropTypes.number,
    uuid: PropTypes.string,
  }).isRequired,
  order: PropTypes.number,
  searchUuid: PropTypes.string,
};
